import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import ClipLoader from "react-spinners/ClipLoader";
import { Plus } from "lucide-react";
import DropdownActions from "../../components/DropdownActions";
import PostActions from "../../components/LikeCommentActions";
import { useParams } from "react-router-dom";
import JobModal from "./JobModal";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const EXPERIENCE_LEVELS = [
  { value: "entry", label: "Entry Level (0-2 years)" },
  { value: "mid", label: "Mid Level (2-5 years)" },
  { value: "senior", label: "Senior Level (5-10 years)" },
  { value: "lead", label: "Lead Level (10+ years)" },
];

const JOB_TYPES = [
  { value: "full_time", label: "Full Time" },
  { value: "part_time", label: "Part Time" },
  { value: "contract", label: "Contract" },
  { value: "freelance", label: "Freelance" },
  { value: "internship", label: "Internship" },
];

const REQUIRED_FIELDS = [
  { name: "title", label: "Post Title" },
  { name: "job_title", label: "Job Title" },
  { name: "company_name", label: "Company Name" },
  { name: "content", label: "Job Description" },
  { name: "experience_level", label: "Experience Level" },
  { name: "job_type", label: "Job Type" },
  { name: "skills_required", label: "Skills" },
  { name: "qualifications", label: "Qualifications" },
  { name: "application_email", label: "Application Email" },
  { name: "salary_min", label: "Salary" },
  { name: "salary_max", label: "Salary" },
  { name: "application_url", label: "Application URL" },
  { name: "location", label: "Location" },
  { name: "remote_allowed", label: "Remote" },
];

export const getExperienceLabel = (value) => {
  const exp = EXPERIENCE_LEVELS.find((e) => e.value === value);
  return exp ? exp.label : "Not specified";
};

const Jobs = () => {
  const [jobs, setJobs] = useState([]);
  const { userId: id } = useParams();
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    job_title: "",
    company_name: "",
    location: "",
    remote_allowed: false,
    salary_min: "",
    salary_max: "",
    currency: "USD",
    experience_level: "",
    job_type: "",
    skills_list: "",
    skills_required: "",
    qualifications: "",
    application_email: "",
    application_url: "",
    content: "",
  });

  const modalRef = useRef(null);
  const token = localStorage.getItem("access_token");

  const fetchJobs = useCallback(
    async (userId = null) => {
      try {
        let jobsFromApi = [];

        if (userId) {
          const res = await axios.get(
            `${BASE_URL}/user/profile/all-profiles?id=${userId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          jobsFromApi = res.data?.data?.posts || [];
        } else {
          const res = await axios.get(`${BASE_URL}/user/posts/`, {
            headers: { Authorization: `Bearer ${token}`,"x-api-key": process.env.REACT_APP_X_API_KEY },
          });
          jobsFromApi = res.data?.results || [];
        }
        const jobPosts = jobsFromApi.filter((p) => p.post_type === "job");

        setJobs(jobPosts);
      } catch (err) {
        console.error("Failed to load jobs", err);
        toast.error("Failed to load jobs");
      } finally {
        setLoading(false);
      }
    },
    [BASE_URL, token]
  );

  const fetchCurrentUser = useCallback(async () => {
    try {
      const res = await fetch(`${BASE_URL}/user/profile/create-profile/`, {
        headers: { Authorization: `Bearer ${token}` ,  "x-api-key": process.env.REACT_APP_X_API_KEY,},
      });
      const data = await res.json();
      if (res.ok) {
        setCurrentUser(data.data);
        setCurrentUserId(data.data.id);
        localStorage.setItem("user_id", data.data?.user);
      } else {
        console.error("Failed to fetch current user");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error getting user profile.");
    }
  }, [BASE_URL, token]);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  useEffect(() => {
    fetchJobs(id);
  }, [fetchJobs, id]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        setIsAddModalOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleEditJob = (job) => {
    setEditingJob(job);
    setFormData({
      title: job.title,
      job_title: job.job_title || "",
      company_name: job.company_name,
      location: job.location,
      remote_allowed: job.remote_allowed,
      salary_min: job.salary_min,
      salary_max: job.salary_max,
      currency: job.currency,
      experience_level: job.experience_level || "",
      job_type: job.job_type || "",
      qualifications: job.qualifications,
      skills_required: Array.isArray(job.skills_required)
        ? job.skills_required.join(", ")
        : job.skills_required || "",
      application_email: job.application_email,
      application_url: job.application_url,
      content: job.content,
    });
    setIsAddModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSaveJob = async () => {
    const errors = {};
    for (let field of REQUIRED_FIELDS) {
      const value = formData[field.name];

      if (!value || value.toString().trim() === "") {
        toast.error(`${field.label} is required`);
        return;
      }
    }
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) return;

    if (!/^\S+@\S+\.\S+$/.test(formData.application_email)) {
      toast.error("Invalid email format");
      return;
    }

    const urlPattern =
      /^(https?:\/\/)?([\w\-])+\.{1}([a-zA-Z]{2,63})([\/\w\-.?=&]*)*\/?$/;
    if (
      formData.application_url &&
      !urlPattern.test(formData.application_url)
    ) {
      toast.error("Invalid URL format");
      return;
    }

    if (
      formData.salary_min &&
      formData.salary_max &&
      Number(formData.salary_min) > Number(formData.salary_max)
    ) {
      toast.error("Salary Min cannot be greater than Salary Max");
      return;
    }
    const currencyRegex = /^[A-Z]{3}$/;

   if (!currencyRegex.test(formData.currency?.toUpperCase())) {
  toast.error("Currency must be a valid 3-letter code (e.g., USD, PKR, EUR).");
  return;
}
//  if (!formData.skills_required.includes(",")) {
//       toast.error("Please separate skills with commas. Example: React.js, JavaScript, CSS");
//       return;
//     }
    const {
      title,
      job_title,
      company_name,
      location,
      remote_allowed,
      salary_min,
      salary_max,
      currency,
      experience_level,
      job_type,
      skills_required,
      qualifications,
      application_email,
      application_url,
      content,
    } = formData;

    const normalizedJobType = job_type
      ? job_type.toLowerCase().replace(" ", "_")
      : "";

    const payload = {
      title,
      content,
      post_type: "job",
      job_title,
      company_name,
      location,
      remote_allowed,
      salary_min,
      salary_max,
      currency,
      experience_level,
      job_type: normalizedJobType,
      skills_required: Array.isArray(formData.skills_required)
        ? formData.skills_required.join(", ")
        : formData.skills_required,
      qualifications,
      application_email,
      application_url,
    };

    try {
      setCreating(true);
      if (editingJob) {
        await axios.patch(
          `${BASE_URL}/user/posts/`,
          { id: editingJob.id, ...payload },
          {
            headers: { Authorization: `Bearer ${token}`,"x-api-key": process.env.REACT_APP_X_API_KEY },
          }
        );
        toast.success("Job updated successfully!");
      } else {
        await axios.post(`${BASE_URL}/user/posts/`, payload, {
          headers: { Authorization: `Bearer ${token}`,"x-api-key": process.env.REACT_APP_X_API_KEY },
        });
        toast.success("Job created successfully!");
      }

      setFormData({
        title: "",
        job_title: "",
        company_name: "",
        location: "",
        remote_allowed: false,
        salary_min: "",
        salary_max: "",
        currency: "USD",
        experience_level: "",
        job_type: "",
        skills_required: "",
        qualifications: "",
        application_email: "",
        application_url: "",
        content: "",
      });
      setJobs((prev) => {
        if (editingJob) {
          return prev.map((j) =>
            j.id === editingJob.id ? { ...j, ...formData } : j
          );
        } else {
          return [{ ...formData, id: Date.now() }, ...prev]; // temp id if backend gives real id, replace it
        }
      });
      setEditingJob(null);
      setIsAddModalOpen(false);
      fetchJobs();
    } catch (err) {
      console.error("Job save failed:", err.response?.data || err.message);
      toast.error(editingJob ? "Failed to update job" : "Failed to create job");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteJob = async (id) => {
    try {
      await axios.delete(`${BASE_URL}/user/posts/`, {
        headers: { Authorization: `Bearer ${token}`,"x-api-key": process.env.REACT_APP_X_API_KEY },
        data: { id },
      });
      toast.success("Job deleted successfully!");
      fetchJobs();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete job");
    }
  };

  if (loading)
    return (
      <div className="jobs-loading">
        <ClipLoader color="#4A90E2" size={40} />
      </div>
    );

  return (
    <div className="w-full max-w-[1050px] ">
      <div className="flex justify-end mb-5">
  {!id && (
    <button
      className="bg-[#004182] text-white p-3 rounded-full font-medium cursor-pointer border border-[#00449d] flex items-center justify-center"
      onClick={() => {
        setEditingJob(null);
        setFormData({
          title: "",
          job_title: "",
          company_name: "",
          location: "",
          remote_allowed: false,
          salary_min: "",
          salary_max: "",
          currency: "USD",
          experience_level: "",
          job_type: "",
          skills_required: "",
          qualifications: "",
          application_email: "",
          application_url: "",
          content: "",
        });
        setIsAddModalOpen(true);
      }}
    >
      <Plus size={18} />
    </button>
  )}
</div>


       {jobs.length === 0 && (
    <div className="flex flex-col justify-center items-center  py-12 bg-white rounded-xl border border-gray-200">
      <div className="w-16 h-16 bg-[#e8f0f7] rounded-xl flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-[#0f6abf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">No jobs yet</h3>
      <p className="text-gray-500 text-sm mb-4">Be the first to post a job opportunity</p>
    </div>
  )}
      {jobs.map((job) => (
        
        
         <div key={job.id} className="relative grid grid-cols-1  gap-4 bg-white rounded-xl border border-gray-200 p-5">
           <div className="absolute top-[-35px] right-0 flex items-center gap-2 mt-1">
                  {Number(job.user?.profile_id) === Number(currentUserId) && (
                    <div >
                      <DropdownActions
                        onEdit={() => handleEditJob(job)}
                        onDelete={() => handleDeleteJob(job.id)}
                      />
                    </div>
                  )}
                 
                </div>
        
           <div className="ml-2 inline-flex items-center gap-2    rounded">
  <h1 className="text-lg font-bold text-gray-900">
    {job.title}
  </h1>
  </div>
            <div>
             
             <div className="prose prose-lg max-w-none bg-white p-6 rounded-xl  border border-gray-50">
  <div
    className="text-gray-800 leading-relaxed text-lg"
    dangerouslySetInnerHTML={{
      __html: job.content?.replace(/<\/?p>/g, ''),
    }}
  />
</div>
            </div>
  <div className="mt-6 border-t border-gray-100 pt-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Header */}
    <div className="col-span-1 sm:col-span-2">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-[#e8f0f7] rounded-lg flex items-center justify-center">
          <svg className="w-5 h-5 text-[#0f6abf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <h4 className="text-sm font-semibold text-gray-900">Job Details</h4>
      </div>
    </div>
    {/* Left Col */}
      <div className="space-y-3">
      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
        <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <div>
          <p className="text-xs text-gray-500">Job Title</p>
          <p className="text-sm font-medium text-gray-900">{job.job_title}</p>
        </div>
      </div>

      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
        <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
        <div>
          <p className="text-xs text-gray-500">Company</p>
          <p className="text-sm font-medium text-gray-900">{job.company_name}</p>
        </div>
      </div>

      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
        <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>
        <div>
          <p className="text-xs text-gray-500">Location</p>
          <p className="text-sm font-medium text-gray-900">{job.location}</p>
        </div>
      </div>
    </div>
     <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-xs text-gray-500">Salary</p>
              <p className="text-sm font-medium text-gray-900">
                {job.salary_min && job.salary_max
                  ? `${job.salary_min} - ${job.salary_max} ${job.currency}`
                  : "Not specified"}
              </p>
            </div>
          </div>
    
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <div>
              <p className="text-xs text-gray-500">Skills</p>
              <p className="text-sm font-medium text-gray-900">
                {Array.isArray(job.skills_required) && job.skills_required.length
                  ? job.skills_required.join(", ")
                  : job.skills_required || "Not specified"}
              </p>
            </div>
          </div>
    
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-xs text-gray-500">Experience</p>
              <p className="text-sm font-medium text-gray-900">{getExperienceLabel(job.experience_level)}</p>
            </div>
          </div>
        </div>
         {/* Bottom Section */}
    <div className="col-span-1 sm:col-span-2 pt-4 border-t border-gray-100">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {job.remote_allowed !== undefined && (
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
            </div>
            <div>
              <p className="text-xs text-gray-500">Remote Work</p>
              <span className={`text-sm font-medium ${
                job.remote_allowed ? 'text-green-600' : 'text-gray-900'
              }`}>
                {job.remote_allowed ? "Allowed" : "Not allowed"}
              </span>
            </div>
          </div>
        )}

        {job.qualifications && (
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-xs text-gray-500">Qualifications</p>
              <p className="text-sm font-medium text-gray-900">{job.qualifications}</p>
            </div>
          </div>
        )}

        {job.application_email && (
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89-4.26a2 2 0 012.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <p className="text-xs text-gray-500">Contact Email</p>
              <p className="text-sm font-medium text-gray-900">{job.application_email}</p>
            </div>
          </div>
        )}

        {job.application_url && (
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </div>
            <div>
              <p className="text-xs text-gray-500">Application URL</p>
              <a 
                href={job.application_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm font-medium text-[#0f6abf] hover:underline"
              >
                View Application
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
      </div>
  </div>
           
          
        
          
        
            <PostActions
            postId={job.id}
            type="post"
            is_Liked={job.is_liked === true}
            likeCount={job.like_count ?? job.total_likes ?? 0}
            currentUser={job.user?.id}
          />
      
          </div>
       
      ))}

      <JobModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        formData={formData}
        onChange={handleChange}
        onSave={handleSaveJob}
        creating={creating}
        editingJob={editingJob}
        formErrors={formErrors}
        ref={modalRef}
      />
    </div>
  );
};

export default Jobs;
